import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "../../state/auth";
import type { ColorKey, SourceKey, WidgetConfig, WidgetSize, WidgetType } from "./types";

// ---------------------------------------------------------------------------
// GuardAI report endpoints live at /api/reports/* (outside the /api/v1 client),
// so we call them with fetch + the same fallback auth headers the rest of the
// dashboard uses (Bearer token, x-user-email, optional reports API key).
// ---------------------------------------------------------------------------
function authHeaders(): Record<string, string> {
  const { user, token } = useAuthStore.getState();
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (user?.email) headers["x-user-email"] = user.email;
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const key = (import.meta as any).env?.VITE_REPORTS_API_KEY;
  if (key) headers["x-api-key"] = key;
  return headers;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      msg = j.error || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json();
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: authHeaders() });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      msg = j.error || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json();
}

export interface DataRow {
  label: string;
  value: number;
}

export interface DataResponse {
  source: string;
  metric: string;
  groupBy: string | null;
  rows: DataRow[];
  total: number;
}

export interface DataQuery {
  source: SourceKey;
  metric: string;
  groupBy?: string | null;
  filters?: Record<string, unknown>;
  dateRange?: { start?: string; end?: string };
  sort?: string;
  limit?: number;
}

/** Strip empty filter values so the query key stays stable. */
function cleanFilters(filters: Record<string, unknown> = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") out[k] = v;
  }
  return out;
}

export function fetchReportData(q: DataQuery): Promise<DataResponse> {
  return postJson<DataResponse>("/api/reports/data", {
    source: q.source,
    metric: q.metric,
    groupBy: q.groupBy ?? null,
    filters: cleanFilters(q.filters),
    dateRange: q.dateRange ?? {},
    sort: q.sort ?? "value_desc",
    limit: q.limit ?? 50,
  });
}

// ---------------------------------------------------------------------------
// Saved reports / templates.
// ---------------------------------------------------------------------------
export interface SavedReportSummary {
  id: string;
  title: string;
  author_name: string | null;
  updated_at: string;
  is_template: boolean;
  config: any;
}

export function listSavedReports(): Promise<{ reports: SavedReportSummary[] }> {
  return getJson("/api/reports/saved");
}

export function saveReport(payload: {
  id?: string | null;
  title: string;
  is_template: boolean;
  config: unknown;
}): Promise<{ report: { id: string } }> {
  return postJson("/api/reports/saved", payload);
}

export async function deleteReport(id: string): Promise<void> {
  const res = await fetch(`/api/reports/saved?id=${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Delete failed (${res.status})`);
}

// ---------------------------------------------------------------------------
// Report-wide data context: collects each widget's loaded rows so the AI
// Summary widget can describe the whole report, exposes the shared date range,
// and the optional hospital scope.
// ---------------------------------------------------------------------------
export interface CacheEntry {
  title: string;
  source: string;
  metric: string;
  groupBy: string | null;
  rows: DataRow[];
  total: number;
}

interface ReportDataCtx {
  range: { start?: string; end?: string };
  hospitalId?: string;
  register: (id: string, entry: CacheEntry) => void;
  snapshot: () => CacheEntry[];
}

const Ctx = createContext<ReportDataCtx | null>(null);

export function ReportDataProvider({
  range,
  hospitalId,
  children,
}: {
  range: { start?: string; end?: string };
  hospitalId?: string;
  children: ReactNode;
}) {
  const cache = useRef<Record<string, CacheEntry>>({});
  const rangeKey = `${range.start || ""}|${range.end || ""}|${hospitalId || ""}`;
  const value = useMemo<ReportDataCtx>(
    () => ({
      range,
      hospitalId,
      register: (id, entry) => {
        cache.current[id] = entry;
      },
      snapshot: () => Object.values(cache.current),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangeKey]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useReportData(): ReportDataCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useReportData must be used within ReportDataProvider");
  return ctx;
}

/** Merge report-wide hospital scope into a widget's filters (widget wins). */
function withHospital(filters: Record<string, unknown> | undefined, hospitalId?: string) {
  const f = { ...(filters || {}) };
  if (hospitalId && (f.hospital_id == null || f.hospital_id === "")) f.hospital_id = hospitalId;
  return f;
}

/**
 * Fetch the data for one widget, merging the report-wide date range + hospital
 * scope, and register the result for the AI Summary widget.
 */
export function useWidgetData(config: WidgetConfig) {
  const { range, hospitalId, register } = useReportData();

  const query: DataQuery = {
    source: config.source,
    metric: config.metric,
    groupBy: config.groupBy,
    filters: withHospital(config.filters, hospitalId),
    dateRange: range,
    sort: config.sort,
    limit: config.limit,
  };

  const result = useQuery({
    queryKey: ["report-data", query],
    queryFn: () => fetchReportData(query),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (result.data) {
      register(config.id, {
        title: config.title,
        source: config.source,
        metric: config.metric,
        groupBy: config.groupBy,
        rows: result.data.rows,
        total: result.data.total,
      });
    }
  }, [result.data, config.id, config.title, config.source, config.metric, config.groupBy, register]);

  return result;
}

/** Single-total fetch (for metric cards and KPI rows). */
export function useTotal(source: SourceKey, metric: string, filters?: Record<string, unknown>) {
  const { range, hospitalId } = useReportData();
  const merged = withHospital(filters, hospitalId);
  return useQuery({
    queryKey: ["report-total", source, metric, range, merged],
    queryFn: () => fetchReportData({ source, metric, groupBy: null, dateRange: range, filters: merged }),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// AI assistant — natural-language / sketch → report layout + walkthrough.
// ---------------------------------------------------------------------------
export interface AiWidgetSpec {
  type: WidgetType;
  title?: string;
  source?: SourceKey;
  metric?: string;
  groupBy?: string | null;
  size?: WidgetSize;
  color?: ColorKey;
}

export interface AiStep {
  title: string;
  detail: string;
  target?: string;
}

export interface AiAssistResult {
  available: boolean;
  message: string;
  /** dateRange: the period the request named (inclusive ET dates); null = all time. */
  report: { title: string; subtitle: string; dateRange?: { start?: string; end?: string } | null; widgets: AiWidgetSpec[] };
  steps: AiStep[];
}

export function aiAssist(prompt: string, image?: string): Promise<AiAssistResult> {
  return postJson<AiAssistResult>("/api/reports/ai-assist", { prompt, image });
}

export async function generateSummary(
  title: string,
  widgets: CacheEntry[]
): Promise<{ available: boolean; text: string }> {
  return postJson<{ available: boolean; text: string }>("/api/reports/generate", {
    mode: "summary",
    title,
    context: { widgets },
  });
}
